import { isBlocksStep } from "@pathwright/blocks-engine/src/utils/utils"
import { flattenEdges } from "@pathwright/utils"
import { useMemo } from "react"
import {
  CoverPagePathItemFragment as CoverPagePreviewPathItem,
  CoverPageResourceFragment as CoverPageResource,
  PathItemType
} from "@pathwright/graphql-client/src/generated"
import { createPathFromItems } from "../../path/utils"
import { useCoverPageContext } from "../CoverPageProvider"
import { PathItems, PreviewItem } from "./types"

export type CoverPagePreviewNavItem =
  | { source_id: "path" }
  | { source_id: number }

export type UseCoverPagePreviewDataReturn = {
  galleryItems: PreviewItem[]
  previewNavItems: CoverPagePreviewNavItem[]
  canPreview: boolean
  hasPreviewableSteps: boolean
  pathItems: PathItems
}

export const useCoverPagePreviewData = ({
  resource
}: {
  resource?: CoverPageResource | null
}): UseCoverPagePreviewDataReturn => {
  const path = resource?.preview_group?.path

  const flattenedPathItems = useMemo(
    () => flattenEdges(path?.items || []) as CoverPagePreviewPathItem[],
    [path?.items]
  )

  const pathItems = useMemo(
    () => createPathFromItems(flattenedPathItems),
    [flattenedPathItems]
  )

  const previewableSteps = useMemo(
    () =>
      pathItems.reduce((acc, item) => {
        if (item.items) {
          acc.push(
            ...item.items.filter(
              (i) =>
                i.is_previewable &&
                i.type === PathItemType.Step &&
                isBlocksStep(i)
            )
          )
        }
        return acc
      }, [] as CoverPagePreviewPathItem[]),
    [pathItems]
  )

  const pathHasSteps = useMemo(
    () => flattenedPathItems.some((item) => item.type === PathItemType.Step),
    [flattenedPathItems]
  )

  const canPreview = previewableSteps?.length > 0 || pathHasSteps

  const previewNavItems = useMemo(
    () =>
      canPreview
        ? [
            { source_id: "path" as const },
            ...previewableSteps.map((s) => ({
              source_id: s.source_id as number
            }))
          ]
        : [],
    [canPreview, previewableSteps]
  )

  const galleryItems: PreviewItem[] = useMemo(
    () =>
      canPreview
        ? [
            {
              type: "path",
              data: pathItems
            },
            ...previewableSteps.map((step) => ({
              type: "step" as "step",
              data: step
            }))
          ]
        : [],
    [canPreview, pathItems, previewableSteps]
  )

  return {
    galleryItems,
    previewNavItems,
    canPreview,
    pathItems,
    hasPreviewableSteps: previewableSteps.length > 0
  }
}

export const useCoverPagePreviewItems = () => {
  const resource = useCoverPageContext((v) => v.resource)
  return useCoverPagePreviewData({ resource })
}
